<template>
  <div class="modal fade receipting-modal" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content border-0 shadow">
        <!-- Modal Header -->
        <div class="modal-header border-0 py-3 modal-gradient-header">
          <div class="d-flex align-items-center">
            <div class="modal-icon me-3">
              <i class="bx bx-clipboard text-white"></i>
            </div>
            <div>
              <h5 class="modal-title mb-0 fw-semibold text-white">Committee Review</h5>
              <small class="text-white opacity-75">{{ application.billNo }} - {{ application.businessName }}</small>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="badge bg-warning text-dark rounded-pill px-3">Pending Review</span>
            <button type="button" class="btn-close btn-close-white ms-2" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
        </div>

        <!-- Modal Body -->
        <div class="modal-body p-0">
          <div class="modal-form-scroll">
            <!-- Application Timeline -->
            <div class="timeline-section bg-light py-3 px-4 border-bottom">
              <h6 class="fw-semibold text-muted mb-3 small text-uppercase">
                <i class="bx bx-git-branch me-1"></i> Application Progress
              </h6>
              <div class="timeline-wrapper">
                <div class="timeline-track">
                  <div v-for="(item, index) in applicationStatuses" :key="index" 
                       class="timeline-item" :class="getTimelineClass(item)">
                    <div class="timeline-marker">
                      <i v-if="item.completed === 'true'" class="bx bx-check"></i>
                      <i v-else-if="item.completed === 'active'" class="bx bx-loader-circle bx-spin"></i>
                      <span v-else>{{ index + 1 }}</span>
                    </div>
                    <div class="timeline-content">
                      <div class="timeline-title">{{ item.status }}</div>
                      <div class="timeline-date" v-if="item.completed === 'true'">
                        {{ formatDate(item.updatedDate) }}
                      </div>
                      <div class="timeline-officer" v-if="item.issuingOfficer">
                        {{ item.issuingOfficer }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="p-4">
              <div class="row g-4">
                <!-- Business Details -->
                <div class="col-md-6">
                  <div class="info-card h-100">
                    <div class="info-card-header">
                      <i class="bx bx-briefcase-alt-2 text-primary"></i>
                      <span>Business Details</span>
                    </div>
                    <div class="info-card-body">
                      <div class="info-row">
                        <span class="info-label">Business Name</span>
                        <span class="info-value fw-semibold">{{ application.businessName || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Business ID</span>
                        <span class="info-value">{{ application.businessID || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Category</span>
                        <span class="info-value">{{ application.businessCategory || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Sub Category</span>
                        <span class="info-value small">{{ application.businessSubCategory || '-' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Location Details -->
                <div class="col-md-6">
                  <div class="info-card h-100">
                    <div class="info-card-header">
                      <i class="bx bx-map text-success"></i>
                      <span>Location Details</span>
                    </div>
                    <div class="info-card-body">
                      <div class="info-row">
                        <span class="info-label">Sub County</span>
                        <span class="info-value">{{ application.subCountyName || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Ward</span>
                        <span class="info-value">{{ application.wardName || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Plot Number</span>
                        <span class="info-value">{{ application.plotNumber || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Fiscal Year</span>
                        <span class="info-value">{{ application.fiscalYear || '-' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Licence Application Details -->
                <div class="col-md-6">
                  <div class="info-card h-100">
                    <div class="info-card-header">
                      <i class="bx bx-id-card text-info"></i>
                      <span>Licence Details</span>
                    </div>
                    <div class="info-card-body">
                      <div class="info-row">
                        <span class="info-label">Date Inspected</span>
                        <span class="info-value">{{ formatDate(application.dateInspected) }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Licence Category</span>
                        <span class="info-value">{{ application.licenceCategory || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Licence Sub Category</span>
                        <span class="info-value small">{{ application.licenceSubCategory || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Duration</span>
                        <span class="info-value">
                          <span class="badge bg-primary">{{ application.licenceDuration || '-' }}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Invoice Details -->
                <div class="col-md-6">
                  <div class="info-card h-100">
                    <div class="info-card-header">
                      <i class="bx bx-receipt text-warning"></i>
                      <span>Invoice Details</span>
                    </div>
                    <div class="info-card-body">
                      <div class="info-row">
                        <span class="info-label">Invoice No.</span>
                        <span class="info-value text-primary fw-semibold">{{ application.billNo || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Invoice Amount</span>
                        <span class="info-value fw-bold">{{ formatCurrency(application.billTotal) }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Amount Paid</span>
                        <span class="info-value text-success">{{ formatCurrency(application.receiptAmount) }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Balance</span>
                        <span class="info-value" :class="parseFloat(application.billBalance) > 0 ? 'text-danger' : 'text-success'">
                          {{ formatCurrency(application.billBalance) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Inspection Notes -->
                <div class="col-12">
                  <div class="info-card">
                    <div class="info-card-header bg-info-subtle">
                      <i class="bx bx-search-alt text-info"></i>
                      <span>Inspection Notes</span>
                    </div>
                    <div class="info-card-body">
                      <div class="inspection-notes p-3 bg-light rounded">
                        <p class="mb-2">{{ application.comments || 'No inspection notes available.' }}</p>
                        <hr class="my-2">
                        <small class="text-muted">
                          <i class="bx bx-user me-1"></i> Inspected by: 
                          <strong>{{ application.issuingOfficer || '-' }}</strong>
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Committee Review Section -->
                <div class="col-12">
                  <div class="review-section">
                    <div class="review-header">
                      <i class="bx bx-edit-alt"></i>
                      <span>Committee Review Outcome</span>
                    </div>
                    <div class="review-body">
                      <!-- Minutes Upload Row -->
                      <div class="row g-3 mb-4">
                        <div class="col-md-6">
                          <label class="form-label fw-semibold">Minutes Number <span class="text-danger">*</span></label>
                          <input v-model="minutesNumber" type="text" class="form-control" 
                                 placeholder="Enter minutes reference number">
                        </div>
                        <div class="col-md-6">
                          <label class="form-label fw-semibold">Upload Minutes Document</label>
                          <input @change="handleFileChange($event)" class="form-control" type="file" 
                                 accept=".pdf,.doc,.docx">
                          <small class="text-muted">Accepted formats: PDF, DOC, DOCX</small>
                        </div>
                      </div>

                      <!-- Decision Selection -->
                      <div class="decision-section mb-4">
                        <label class="form-label fw-semibold">Committee Decision <span class="text-danger">*</span></label>
                        <div class="decision-cards">
                          <div class="decision-card" :class="{ active: approvedStatus === 'Yes', 'border-success': approvedStatus === 'Yes' }"
                               @click="approvedStatus = 'Yes'">
                            <div class="decision-icon bg-success-subtle">
                              <i class="bx bx-check-circle text-success"></i>
                            </div>
                            <div class="decision-text">
                              <strong>Approve</strong>
                              <small>Application meets all requirements</small>
                            </div>
                            <input type="radio" v-model="approvedStatus" value="Yes" class="form-check-input">
                          </div>
                          <div class="decision-card" :class="{ active: approvedStatus === 'No', 'border-danger': approvedStatus === 'No' }"
                               @click="approvedStatus = 'No'">
                            <div class="decision-icon bg-danger-subtle">
                              <i class="bx bx-x-circle text-danger"></i>
                            </div>
                            <div class="decision-text">
                              <strong>Decline</strong>
                              <small>Application does not meet requirements</small>
                            </div>
                            <input type="radio" v-model="approvedStatus" value="No" class="form-check-input">
                          </div>
                        </div>
                      </div>

                      <!-- Approval Section -->
                      <div v-if="approvedStatus === 'Yes'" class="approval-section">
                        <!-- Eligibility Confirmation -->
                        <div class="eligibility-card mb-4">
                          <div class="eligibility-header">
                            <i class="bx bx-shield-quarter text-primary"></i>
                            <span>Confirmation of Applicant Eligibility</span>
                          </div>
                          <div class="eligibility-body">
                            <p class="text-muted mb-3">
                              By checking the box below, you confirm that the applicant is eligible to proceed with 
                              payment for the license processing, having met all requirements as stipulated by the 
                              <strong>Alcoholic Drinks Control Act</strong>.
                            </p>
                            <div class="form-check">
                              <input v-model="eligibilityCheck" class="form-check-input" type="checkbox" id="eligibility-modal">
                              <label class="form-check-label fw-semibold" for="eligibility-modal">
                                I confirm the applicant's eligibility under the Alcoholic Drinks Control Act
                              </label>
                            </div>
                          </div>
                        </div>

                        <!-- Approval Remarks -->
                        <div class="mb-3">
                          <label class="form-label fw-semibold">Committee Review Notes</label>
                          <textarea v-model="approveRemarks" class="form-control" rows="4" 
                                    placeholder="Enter key notes from the committee review session..."></textarea>
                        </div>
                      </div>

                      <!-- Decline Section -->
                      <div v-if="approvedStatus === 'No'" class="decline-section">
                        <div class="alert alert-danger-soft mb-3">
                          <i class="bx bx-error-circle me-2"></i>
                          <strong>Important:</strong> Please provide detailed reasons for declining this application.
                        </div>
                        <div class="mb-3">
                          <label class="form-label fw-semibold">Reasons for Declining <span class="text-danger">*</span></label>
                          <textarea v-model="declinedReason" class="form-control" rows="4" 
                                    placeholder="Enter the specific reasons for declining this application..."></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Processing Overlay -->
          <div v-if="isProcessing" class="processing-overlay">
            <div class="processing-content">
              <div class="spinner-border text-primary mb-3" role="status"></div>
              <h5>{{ processingMessage }}</h5>
              <p class="text-muted mb-0">Please wait...</p>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light border-0 py-3">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i> Cancel
          </button>
          <button @click="printBill()" type="button" class="btn btn-outline-primary">
            <i class="bx bx-printer me-1"></i> Print Invoice
          </button>
          
          <!-- Approve Button -->
          <button v-if="approvedStatus === 'Yes'" 
                  @click="submitReview('Approve')" 
                  type="button" 
                  class="btn btn-success"
                  :disabled="!canApprove">
            <i class="bx bx-check-circle me-1"></i> Approve Application
          </button>

          <!-- Decline Button -->
          <button v-if="approvedStatus === 'No'" 
                  @click="submitReview('Decline')" 
                  type="button" 
                  class="btn btn-danger"
                  :disabled="!canDecline">
            <i class="bx bx-x-circle me-1"></i> Decline Application
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "ReviewModal",
  props: {
    application: {
      type: Object,
      default: () => ({})
    },
    applicationStatuses: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      minutesNumber: '',
      selectedFile: null,
      approvedStatus: '',
      eligibilityCheck: false,
      approveRemarks: '',
      declinedReason: '',
      isProcessing: false,
      processingMessage: ''
    }
  },
  computed: {
    canApprove() {
      return this.minutesNumber.trim() !== '' && this.eligibilityCheck && this.approveRemarks.trim() !== '';
    },
    canDecline() {
      return this.minutesNumber.trim() !== '' && this.declinedReason.trim() !== '';
    }
  },
  watch: {
    application: {
      handler() {
        this.resetForm();
      },
      deep: true
    }
  },
  methods: {
    resetForm() {
      this.minutesNumber = '';
      this.selectedFile = null;
      this.approvedStatus = '';
      this.eligibilityCheck = false;
      this.approveRemarks = '';
      this.declinedReason = '';
    },

    formatDate(dateString) {
      if (!dateString || dateString === '-') return '-';
      return moment(dateString).format('DD MMM YYYY');
    },

    formatCurrency(amount) {
      if (!amount || isNaN(amount)) return 'KES 0';
      return 'KES ' + parseFloat(amount).toLocaleString('en-KE');
    },

    getTimelineClass(item) {
      if (item.completed === 'true') return 'completed';
      if (item.completed === 'active') return 'active';
      return 'pending';
    },

    handleFileChange(event) {
      this.selectedFile = event.target.files[0];
    },

    printBill() {
      this.$emit('print-bill', this.application.billNo);
    },

    submitReview(reviewStatus) {
      this.isProcessing = true;
      this.processingMessage = reviewStatus === 'Approve' ? 'Approving application...' : 'Processing decline...';

      this.$emit('submit-review', {
        reviewStatus,
        billNo: this.application.billNo,
        businessName: this.application.businessName,
        minutesNumber: this.minutesNumber,
        selectedFile: this.selectedFile,
        approveRemarks: this.approveRemarks,
        declinedReason: this.declinedReason,
        feesAndChargeItems: this.application.feesAndChargeItems
      });
    },

    stopProcessing() {
      this.isProcessing = false;
    }
  }
}
</script>

<style scoped>
/* Modal Header Gradient */
.modal-gradient-header {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.modal-icon {
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

/* Modal Scroll */
.modal-form-scroll {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.modal-form-scroll::-webkit-scrollbar {
  width: 6px;
}

.modal-form-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.modal-form-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

/* Timeline */
.timeline-wrapper {
  overflow-x: auto;
  padding-bottom: 10px;
}

.timeline-track {
  display: flex;
  gap: 0;
  min-width: max-content;
}

.timeline-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
  position: relative;
  padding: 0 10px;
}

.timeline-item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 15px;
  left: 60%;
  width: 80%;
  height: 2px;
  background: #e9ecef;
}

.timeline-item.completed:not(:last-child)::after {
  background: #28a745;
}

.timeline-marker {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  background: #e9ecef;
  color: #6c757d;
  z-index: 1;
}

.timeline-item.completed .timeline-marker {
  background: #28a745;
  color: white;
}

.timeline-item.active .timeline-marker {
  background: #007bff;
  color: white;
}

.timeline-content {
  text-align: center;
  margin-top: 8px;
}

.timeline-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #495057;
}

.timeline-date {
  font-size: 0.65rem;
  color: #6c757d;
}

.timeline-officer {
  font-size: 0.65rem;
  color: #28a745;
}

/* Info Card */
.info-card {
  background: #f8f9fa;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: white;
  font-weight: 600;
  font-size: 0.875rem;
  color: #495057;
  border-bottom: 2px solid #e9ecef;
}

.info-card-header i {
  font-size: 1.25rem;
}

.info-card-body {
  padding: 1rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;
  border-bottom: 1px dashed #e9ecef;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  color: #6c757d;
  font-size: 0.8rem;
}

.info-value {
  font-size: 0.85rem;
  text-align: right;
  max-width: 60%;
}

/* Review Section */
.review-section {
  background: white;
  border-radius: 12px;
  border: 2px solid #ffc107;
  overflow: hidden;
}

.review-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%);
  font-weight: 600;
  color: #856404;
}

.review-header i {
  font-size: 1.5rem;
}

.review-body {
  padding: 1.5rem;
}

/* Decision Cards */
.decision-cards {
  display: flex;
  gap: 1rem;
}

.decision-card {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.decision-card:hover {
  background: #f8f9fa;
}

.decision-card.active {
  background: #f8f9fa;
}

.decision-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.decision-text {
  flex: 1;
}

.decision-text strong {
  display: block;
  font-size: 1rem;
}

.decision-text small {
  color: #6c757d;
}

/* Eligibility Card */
.eligibility-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  overflow: hidden;
}

.eligibility-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: white;
  font-weight: 600;
  border-bottom: 1px solid #dee2e6;
}

.eligibility-body {
  padding: 1rem;
}

/* Alert Soft */
.alert-danger-soft {
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.2);
  color: #842029;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
}

/* Processing Overlay */
.processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.processing-content {
  text-align: center;
}

/* Backgrounds */
.bg-success-subtle { background-color: rgba(25, 135, 84, 0.15); }
.bg-danger-subtle { background-color: rgba(220, 53, 69, 0.15); }
.bg-info-subtle { background-color: rgba(13, 202, 240, 0.15); }

/* Responsive */
@media (max-width: 768px) {
  .decision-cards {
    flex-direction: column;
  }
}
</style>
